import { Controller, useForm } from 'react-hook-form';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Select from 'components/Select';

import attachSvg from '/attach.svg';

import Button from 'components/Button';
import Modal from 'components/Modal';
import helpMutation from 'api/mutations/helpMutation';

import cancelIcon from '/trashHelp.svg';

interface IData {
  category: { value: string; label: string };
  message: string;
  files: File;
}

const Help = () => {
  const { t } = useTranslation();
  const { handleSubmit, control, watch } = useForm<IData>();
  const watchCategory = watch(['category']);
  const watchMessage = watch(['message']);
  const { mutate, isPending } = helpMutation();
  const [modal, setModal] = useState<boolean>(false);
  const [file, setFile] = useState<File | undefined>(undefined);
  const ref = useRef<any>(null);

  const options = [
    { value: 'bugs',
      label: t('bugsErrors') },
    { value: 'feedback',
      label: t('feedback') },
  ];

  const resetFileInput = () => {
    if (ref?.current) {
      ref.current.value = null;
    }
  };

  const onSubmit = (data: IData) => {
    const body = {
      feedbackType: data.category.value,
      text: data.message,
      files: file,
    };
    mutate(
      file?.name
        ? body
        : {
          feedbackType: data.category.value,
          text: data.message,
        },
      {
        onSuccess: () => {
          setModal(true);
        },
      },
    );
  };

  const disabled = () => !(watchCategory?.[0]?.value && watchMessage?.[0]?.length > 0);

  return (
    <div className="flex items-center justify-center mt-14 md:mx-4 md:my-8 mb-20">
      {modal && (
        <Modal
          saveBtnText={t('goBack')}
          title={`${t('success')}!`}
          text={t('messageSucces')}
          onSaveClick={() => {
            window.location.reload();
          }}
          onCancelClick={() => {
            window.location.reload();
          }}
        />
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-center flex-col border border-primitiveGrey rounded-2xl p-14 md:w-[358px] md:p-2 md:mx-2">
          <div className="text-2xl text-black font-bold mb-3 flex items-center justify-center md:font-normal">
            {t('help')}
          </div>
          <div className="text-secondaryText mb-12 md:text-center">
            {t('helpQuestion')}
          </div>
          <div className="w-full mb-6">
            <Controller
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { onChange } }) => (
                <Select
                  options={options}
                  title={`${t('category')}*`}
                  onChange={onChange}
                  placeholder={t('chooseCategory')}
                />
              )}
              name="category"
            />
          </div>

          <div className="w-full mb-6 relative">
            <div className="text-sm text-secondaryText absolute top-[-10px] left-2 bg-grey">
              {t('message')}*
            </div>
            <Controller
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { onChange, value } }) => (
                <textarea
                  onChange={onChange}
                  value={value}
                  className="w-full h-40 border border-primitiveGrey rounded-xl p-2.5 active:border-primary active:border focus:border-primary outline-none"
                  placeholder={t('typeMessage')}
                />
              )}
              name="message"
            />
          </div>
          <div className="flex items-center justify-start md:whitespace-nowrap">
            <label
              htmlFor="files"
              className="text-primary flex items-center justify-start cursor-pointer"
            >
              <img src={attachSvg} alt="attach" />
              {file?.name ? (
                <span className="text-black font-medium md:whitespace-normal md:text-sm">
                  {file?.name}
                </span>
              ) : (
                <span>{t('attachFile')}</span>
              )}
            </label>
            {file?.name && (
              <span
                className="ml-3 flex items-center justify-center cursor-pointer md:m-0 w-[24px] h-[24px]"
                onClick={() => {
                  setFile(undefined);
                  resetFileInput();
                }}
              >
                <img src={cancelIcon} alt="trash" />
              </span>
            )}
            <input
              id="files"
              type="file"
              ref={ref}
              onChange={(event) => {
                setFile(event?.target?.files?.[0]);
              }}
              style={{ visibility: 'hidden' }}
            />
          </div>
          <div className="justify-center flex mt-10">
            <Button disabled={disabled() || isPending} type="submit">
              {t('sendFeedback')}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Help;
